import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { STORAGE_ACTION, getStorageItem } from '@utils/storage';
import {
  RegisterRfqInput,
  useRegisterRfqMutation,
  CapaErrorCode,
  RfqDrawingFileInput,
  RfqReferenceFileInput,
  RfqPcbAssemblyFileInput,
  RfqPcbFabricationFileInput,
  CapaQuotationServiceType,
  CapaPcbServiceType
} from '@generated/graphql';
import { RootState } from '../modules/index';
import * as actions from '../modules/request/actions';
import {
  IRequestState,
  UploadedDrawingFileType
} from '../modules/request/types';
import useClient from './useClient';
import { useSnackbar } from '@components/Provider';
import { useHistory } from 'react-router-dom';
import { formatDate } from '@utils/formatter';
import { requestEnumToString } from '@utils/enumToString';
import { initialState } from '../modules/request/index';
import useUserActionLog, {
  CapaActivityType,
  Enum_Action_Page_Enum
} from '@hooks/useUserActionLog';
import {
  DesignRequestStageEnum,
  PcbRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';

function useRequest() {
  const isGhost = getStorageItem(STORAGE_ACTION.IS_GHOST_CLIENT);
  const history = useHistory();
  const [registerRfq] = useRegisterRfqMutation();
  const { clientState } = useClient();
  const { enqueueSnackbar } = useSnackbar();
  const { onInsertUserActionLogOne } = useUserActionLog();

  // state
  const requestState = useSelector((state: RootState) => state.request);
  const serviceType = requestState.serviceType;
  const drawingFiles = requestState.drawingFiles;
  const referenceFiles = requestState.uploadedReferenceFiles;
  const isEmptyDrwaingFiles =
    (requestState.uploadedDrawingFiles?.length ?? 0) === 0;
  const isEmptyReferenceFiles = requestState.uploadedReferenceFiles
    ? (requestState.uploadedDrawingFiles?.length ?? 0) === 0
    : true;
  const isDesign = serviceType === CapaQuotationServiceType.Design;
  const isPcb = serviceType === CapaQuotationServiceType.Pcb;
  const pcbServices = (requestState?.pcb?.services ?? []).map(
    (service) => service.serviceType
  );
  const designServices = (requestState?.design?.services ?? []).map(
    (service) => service.serviceType
  );
  const lastRequestQuery: RequestQueryString = {
    serviceType: requestState.serviceType,
    detailedServiceType:
      !isDesign && !isPcb
        ? requestState?.detailedServiceType ?? undefined
        : undefined,
    process: isPcb
      ? pcbServices.toString()
      : isDesign
      ? designServices.toString()
      : undefined,
    stage: isPcb
      ? PcbRequestStageEnum.PROCESS_SELECT
      : isDesign
      ? DesignRequestStageEnum.PROCESS_SELECT
      : undefined
  };
  const preRfqId = requestState.preRfqId;
  const consultingStartMethod = requestState.consultingStartMethod;

  // 도면 파일 가공 옵션 모두 입력된 경우 true
  const isAllDrwaingFilesValid =
    requestState.uploadedDrawingFiles?.every((file) => file.isValid) ?? false;
  const isComplete = requestState.isComplete;

  // 이전 요청이 남아있는지 체크
  const hasNotEndedRequest = useMemo(() => {
    const {
      uploadedDrawingFiles,
      referenceFiles,
      pcb,
      detailedRequest
    } = requestState;
    const {
      CircuitDesign,
      CircuitFabrication,
      CircuitAssembly
    } = CapaPcbServiceType;

    if (requestState.serviceType) {
      /* design, pcb가 아닌 서비스타입은 도면이 업로드 된 경우 이전요청으로 판단 */
      if (!isDesign && !isPcb && (uploadedDrawingFiles ?? []).length > 0) {
        return true;
      }

      /* design의 경우 참고파일이 업로드 된 경우 이전요청으로 판단 */
      if (isDesign && (referenceFiles ?? []).length > 0) {
        return true;
      }

      if (isPcb) {
        const hasCircuitDesign = pcbServices.includes(CircuitDesign);
        const hasCircuitFabrication = pcbServices.includes(CircuitFabrication);
        const hasCircuitAssembly = pcbServices.includes(CircuitAssembly);

        /* pcb의 경우 설계가 포함되면 상세요청내용(detailedRequest) 값 여부로 판단 */
        if (hasCircuitDesign && (detailedRequest ?? '').length > 0) {
          return true;
        }

        /* 설계가 포함되지 않고 제작이 포함되면, 제작 파일 유무로 판단 */
        if (
          !hasCircuitDesign &&
          hasCircuitFabrication &&
          (pcb?.fabricationFiles ?? []).length > 0
        ) {
          return true;
        }

        /* 설계가 포함되지 않고 설계가 포함되면, 설계 파일(BOM) 유무로 판단 */
        if (
          !hasCircuitDesign &&
          hasCircuitAssembly &&
          (pcb?.assemblyFiles ?? []).length > 0
        ) {
          return true;
        }

        return false;
      }

      return false;
    }

    return false;
  }, [requestState, isDesign, isPcb, pcbServices]);

  // dispatch functions
  const dispatch = useDispatch();

  const onCreateRfqFile = useCallback(
    (file: UploadedDrawingFileType) => dispatch(actions.createRfqFile(file)),
    [dispatch]
  );

  const onCreateReferenceFile = useCallback(
    (file: RfqReferenceFileInput & { size: number }) =>
      dispatch(actions.createReferenceFile(file)),
    [dispatch]
  );

  const onDeleteRepresentiveImage = useCallback(
    () => dispatch(actions.deleteRepresentiveImage()),
    [dispatch]
  );

  const onUploadRepresentiveImage = useCallback(
    (file: RfqReferenceFileInput & { size: number }) =>
      dispatch(actions.uploadRepresentiveImage(file)),
    [dispatch]
  );

  const onDeleteRfqFile = useCallback(
    (fileId: string) => dispatch(actions.deleteRfqFile(fileId)),
    [dispatch]
  );

  const onUpdateRfqFile = useCallback(
    (rfqFile: UploadedDrawingFileType) =>
      dispatch(actions.updateRfqFile(rfqFile)),
    [dispatch]
  );

  const onAllUpdateRfqFile = useCallback(
    (rfqFile: UploadedDrawingFileType[]) =>
      dispatch(actions.allUpdateRfqFile(rfqFile)),
    [dispatch]
  );

  const onBatchUpdateRfqFile = useCallback(
    (rfqFile: UploadedDrawingFileType) =>
      dispatch(actions.batchUpdateRfqFile(rfqFile)),
    [dispatch]
  );

  const onUpdateRfqInfo = useCallback(
    (rfqInfo: Partial<IRequestState>) => {
      return dispatch(actions.updateRfqInfo(rfqInfo));
    },
    [dispatch]
  );

  const onCompleteRfq = useCallback(() => dispatch(actions.completeRfq()), [
    dispatch
  ]);

  const onUpdateCompleteRfq = useCallback(
    (state: boolean) => dispatch(actions.updateCompleteRfq(state)),
    [dispatch]
  );

  const onInitRfqState = useCallback(() => dispatch(actions.initRfqState()), [
    dispatch
  ]);

  const onSetProccessingState = useCallback(
    (isOnProcessing: boolean) =>
      dispatch(actions.setProceessingState(isOnProcessing)),
    [dispatch]
  );

  const onUpdateCircuitFabricationFile = useCallback(
    (files: RfqPcbFabricationFileInput[]) => {
      dispatch(actions.updatePcbFabricationFile(files));
    },
    [dispatch]
  );

  const onUpdateCircuitAssemblyFile = useCallback(
    (files: RfqPcbAssemblyFileInput[]) => {
      dispatch(actions.updatePcbAssemblyFile(files));
    },
    [dispatch]
  );

  /* RFQ 작성 내용 처리 */
  const getRequestForInsertRFQ: RegisterRfqInput = useMemo(() => {
    const {
      uploadedDrawingFiles,
      uploadedReferenceFiles,
      detailed_request,
      isComplete,
      design,
      requestPrototyping,
      prototypeQuantity,
      isTest,
      ...rest
    } = requestState;

    return {
      ...rest,
      rfqId: requestState.rfqId as string,
      dueDate: formatDate(requestState.dueDate, 'YYYY-MM-DD'),
      optionalServices:
        requestState.optionalServices?.map((service) => {
          service.quantity = Number(service.quantity);
          return service;
        }) ?? [],
      drawingFiles:
        requestState.uploadedDrawingFiles?.map((file) => {
          return {
            fileId: file.fileId,
            fileName: file.fileName,
            material: file.material,
            detailedMaterial: file.detailedMaterial,
            quantity: Number(file.quantity),
            isValid: undefined,
            recommendation: file.recommendation,
            etc: file.etc
          } as RfqDrawingFileInput;
        }) ?? [],
      referenceFiles:
        requestState.uploadedReferenceFiles?.map((file) => {
          return {
            ...file,
            fileSize: undefined,
            size: undefined
          } as RfqReferenceFileInput;
        }) ?? [],
      maxEstimatedBudget: Number(requestState.maxEstimatedBudget),
      projectPurposeDetail: requestState.projectPurposeDetail ?? ''
    };
  }, [requestState]);

  /* RFQ 작성 완료 (insert mutation 실행) */
  const insertRFQ = useCallback(
    async (callbackFunc?: any) => {
      if (isGhost && isGhost === 'true') return;
      onSetProccessingState(true);
      const result = await registerRfq({
        variables: {
          input: getRequestForInsertRFQ
        }
      });
      /* RFQ Insert 성공 */
      if (result.data?.registerRFQ.result === CapaErrorCode.Success) {
        enqueueSnackbar(
          `${clientState.managerName || ''} 고객님, ${requestEnumToString(
            serviceType
          )} 견적 요청이 성공적으로 완료되었습니다!`,
          { variant: 'success' }
        );
        onInitRfqState();
        history.push('/dashboard', {
          isRfqsubmitted: true
        });
        onSetProccessingState(false);
        return;
      } else {
        if (result.data?.registerRFQ.result === CapaErrorCode.DuplicateRfqId) {
          onInitRfqState();
          history.push('/dashboard', {
            isRfqsubmitted: true
          });
          onSetProccessingState(false);
          return;
        }

        onInsertUserActionLogOne(
          Enum_Action_Page_Enum[
            'ERROR_MESSAGE' as keyof typeof Enum_Action_Page_Enum
          ],
          CapaActivityType.ButtonClick,
          result.data?.registerRFQ
        );

        enqueueSnackbar(`견적요청에 실패하였습니다.`, {
          variant: 'error'
        });
      }

      onSetProccessingState(false);
      return result;
    },
    [
      onInitRfqState,
      clientState.managerName,
      registerRfq,
      enqueueSnackbar,
      getRequestForInsertRFQ,
      history,
      isGhost,
      serviceType,
      onSetProccessingState,
      onInsertUserActionLogOne
    ]
  );

  return {
    initialState,
    requestState,
    isAllDrwaingFilesValid,
    isEmptyDrwaingFiles,
    isEmptyReferenceFiles,
    isComplete,
    serviceType,
    drawingFiles,
    referenceFiles,
    isDesign,
    isPcb,
    pcbServices,
    hasNotEndedRequest,
    lastRequestQuery,
    preRfqId,
    consultingStartMethod,
    onCreateRfqFile,
    onCreateReferenceFile,
    onUploadRepresentiveImage,
    onDeleteRepresentiveImage,
    onDeleteRfqFile,
    onUpdateRfqFile,
    onUpdateRfqInfo,
    onCompleteRfq,
    onUpdateCompleteRfq,
    onInitRfqState,
    onAllUpdateRfqFile,
    onBatchUpdateRfqFile,
    onSetProccessingState,
    onUpdateCircuitFabricationFile,
    onUpdateCircuitAssemblyFile,
    insertRFQ
  };
}

export default useRequest;
